import { Component, Inject, OnDestroy } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { Observable, Subject, Subscription, take, takeUntil } from "rxjs";
import { AssessmentTaskScopeChangesService } from "../../services/assessment_task_scope_changes.service";
import { NZ_MODAL_DATA, NzModalRef } from "ng-zorro-antd/modal";
import { NzSelectComponent } from "ng-zorro-antd/select";
import { AccountSettingsService } from "../../services/account_settings.service";
import { AssessmentAssignmentRole, AssessmentInvitationPayload, ExistingMemberOffer, OrganizationRole, PersistedInvitationResult, StorageOnlyConversionOffer, StorageOnlyConversionResult } from "../../services/assessment_user_access.types";
import { UserAccessError } from "../../services/assessment_user_access.util";
import { LoginService } from "../../services/login.service";
import { assessmentTaskSessionIdentity } from "../../services/assessment_task_session.util";
import { AddUserCompletion, AddUserModalData } from "./add_user_modal.types";
import { assignmentSelectionError } from "./assessment_assignment_form.util";
import { invitationFailureMessage, invitationResultMessage } from "./invitation_result.util";
import { encodeBase64RouteValue } from "../../utils/base64_route_value.util";

@Component({
    selector: "app-add-user-modal",
    templateUrl: "./add_user_modal.component.html",
    styleUrls: ["./add_user_modal.component.scss"]
})
export class AddUserModalComponent implements OnDestroy {
    get assessmentPath(): string | null {
        const form = this.data.access.assessment?.formType.toLowerCase() || "";
        return form.includes("roc") ? "/roc_form" : form.includes("saq") ? "/saq_form" : null;
    }
    get encodedAssessmentId(): string {
        return encodeBase64RouteValue(String(this.data.access.assessment?.productLicenseId || ""));
    }
    readonly form = this.fb.group({
        displayName: ["", [Validators.required, Validators.maxLength(255), Validators.pattern(/\S/)]],
        email: ["", [Validators.required, Validators.email, Validators.maxLength(255)]],
        phoneNumber: ["", Validators.maxLength(50)],
        certificateNumber: ["", Validators.maxLength(100)],
        role: ["User", Validators.required]
    });
    get roleChoices() { return this.data.access.capabilities.invitationOrganizationRoles.filter((role) => role !== "Storage Only"); }
    raiseRoleDropdown(select: NzSelectComponent, open: boolean): void {
        if (!open) return;
        // Connected overlays have their own stacking context below this raised modal.
        const raise = () => {
            const overlay = select.cdkConnectedOverlay?.overlayRef;
            if (select.nzOpen && overlay) {
                overlay.hostElement.style.zIndex = String(Number(this.modal.getConfig().nzZIndex || 1000) + 1);
            }
        };
        if (select.cdkConnectedOverlay.overlayRef?.hasAttached()) raise();
        else select.cdkConnectedOverlay.attach.pipe(take(1), takeUntil(this.destroyed)).subscribe(raise);
    }
    accessLoading = false;
    private accessFailed = false;
    private accessRefreshPending = false;
    private accessRead?: Subscription;
    private accessVersion = 0;
    busy = false;
    confirming = false;
    error = "";
    persisted: PersistedInvitationResult | null = null;
    uncertainSubmission = false;
    canResend = false;
    recoveryMessage = "";
    readonly resultMessage = invitationResultMessage;
    private recoveryVersion = 0;
    private recoveryRead?: Subscription;
    conversion: StorageOnlyConversionOffer | null = null;
    existingMember: ExistingMemberOffer | null = null;
    assignmentRoles: AssessmentAssignmentRole[] = this.data.context.kind === "assessment" && this.data.context.defaultRole
        ? [this.data.context.defaultRole] : [];
    private pending: AssessmentInvitationPayload | null = null;
    private readonly destroyed = new Subject<void>();
    private readonly submissionKey = this.newSubmissionKey();

    constructor(private readonly fb: FormBuilder, private readonly accounts: AccountSettingsService,
        private readonly login: LoginService, @Inject(NZ_MODAL_DATA) readonly data: AddUserModalData,
        private readonly modal: NzModalRef<AddUserModalComponent, AddUserCompletion>,
        scopeChanges: AssessmentTaskScopeChangesService) {
        scopeChanges.watchChanges().pipe(takeUntil(this.destroyed)).subscribe((change) => {
            if (this.data.context.kind !== "assessment" || this.persisted ||
                change.productLicenseId !== null && change.productLicenseId !== this.data.context.productLicenseId) return;
            this.accessRefreshPending = true;
            this.refreshAccess();
        });
        if (this.data.unresolvedInvitation) {
            this.pending = { ...this.data.unresolvedInvitation, desiredRoles: this.data.unresolvedInvitation.desiredRoles ? [...this.data.unresolvedInvitation.desiredRoles] : undefined };
            this.form.patchValue(this.pending);
            this.assignmentRoles = [...(this.pending.desiredRoles ?? [])];
            this.uncertainSubmission = true; this.confirming = true;
            this.form.disable();
        }
    }

    private refreshAccess(): void {
        if (!this.accessRefreshPending || this.busy || this.persisted || !this.current() || this.data.context.kind !== "assessment") return;
        this.accessRefreshPending = false;
        const version = ++this.accessVersion;
        this.accessRead?.unsubscribe();
        this.accessLoading = true;
        this.accessRead = this.accounts.getUserAccess({ productLicenseId: this.data.context.productLicenseId })
            .pipe(takeUntil(this.destroyed)).subscribe({
                next: (access) => {
                    if (version !== this.accessVersion || !this.current()) return;
                    this.data.access = access;
                    this.accessLoading = false; this.accessFailed = false;
                    if (!access.capabilities.canInviteOnAssessment) this.error = "Invitation permission is no longer available for this assessment. Your draft has not been submitted.";
                }, error: () => {
                    if (version !== this.accessVersion || !this.current()) return;
                    this.accessLoading = false; this.accessFailed = true;
                    this.error = "Current invitation permission could not be confirmed. Close and reopen this dialog before submitting.";
                }
            });
    }

    get accessReady(): boolean {
        return !this.accessLoading && !this.accessFailed && !this.accessRefreshPending &&
            (this.assessmentMode ? this.data.access.capabilities.canInviteOnAssessment : this.data.access.capabilities.canInviteWithoutAssessment);
    }

    get assessmentMode(): boolean { return this.data.context.kind === "assessment"; }
    get organizationRole(): OrganizationRole { return (this.data.isGlobalAdmin ? this.form.controls.role.value : "User") as OrganizationRole; }
    get assignmentError(): string {
        return this.assessmentMode ? assignmentSelectionError(this.data.access.assessment, this.assignmentRoles,
            this.existingMember?.existingUser.role ?? this.organizationRole, this.existingMember?.existingUser.id ?? null, true) : "";
    }
    get canSubmit(): boolean {
        return (this.assessmentMode ? this.data.access.capabilities.canInviteOnAssessment && !this.assignmentError
            : this.data.access.capabilities.canInviteWithoutAssessment) &&
            this.accessReady && !this.uncertainSubmission && !this.busy && !this.persisted && this.form.valid && this.roleChoices.some((role) => role === this.form.controls.role.value);
    }

    submit(): void {
        if (!this.current()) return;
        this.form.markAllAsTouched();
        if (!this.canSubmit || this.confirming) return;
        const value = this.form.getRawValue();
        this.pending = { displayName: value.displayName!.trim(), email: value.email!.trim(),
            phoneNumber: value.phoneNumber?.trim() || null, certificateNumber: value.certificateNumber?.trim() || null,
            role: (this.data.isGlobalAdmin ? value.role : "User") as AssessmentInvitationPayload["role"],
            submissionKey: this.submissionKey };
        if (this.data.context.kind === "assessment") {
            this.pending.productLicenseId = this.data.context.productLicenseId;
            this.pending.desiredRoles = [...this.assignmentRoles];
        }
        this.confirming = true;
        this.error = "";
        this.form.disable();
    }

    back(): void {
        if (this.busy || this.persisted || this.uncertainSubmission) return;
        this.confirming = false;
        this.conversion = null;
        this.existingMember = null;
        this.pending = null;
        this.form.enable();
    }

    confirm(): void {
        if (!this.current() || !this.accessReady || !this.uncertainSubmission && this.assignmentError || this.busy || !this.pending || !this.confirming || this.persisted ||
            !this.uncertainSubmission && !this.roleChoices.some((role) => role === this.pending?.role)) return;
        this.setBusy(true);
        this.error = "";
        const payload = !this.uncertainSubmission && this.existingMember ? { ...this.pending, assignExistingUserId: this.existingMember.existingUser.id } : this.pending;
        this.pending = payload;
        const request: Observable<PersistedInvitationResult | StorageOnlyConversionResult> = this.conversion
            ? this.accounts.convertStorageOnlyInvitation(this.pending, this.conversion.userId)
            : this.accounts.inviteUserWithAccess(payload);
        request.pipe(takeUntil(this.destroyed)).subscribe({
            next: (result) => {
                if (!this.current()) return;
                if (result.outcome !== "storage_user_converted") {
                    this.uncertainSubmission = false;
                    this.persisted = result;
                    this.setBusy(false);
                    this.refreshResendPermission();
                    return;
                }
                // Keep confirmation guarded through the modal's closing animation.
                this.modal.destroy(result);
            },
            error: (error: unknown) => {
                if (!this.current()) return;
                this.setBusy(false);
                this.refreshAccess();
                if (!this.conversion) this.uncertainSubmission = Boolean(this.accounts.getUnresolvedInvitation());
                if (this.uncertainSubmission) {
                    this.error = invitationFailureMessage(new UserAccessError("", "INVITATION_OUTCOME_UNKNOWN"));
                    return;
                }
                if (error instanceof UserAccessError && error.storageOnlyConversion && !this.assessmentMode &&
                    this.data.access.capabilities.canInviteWithoutAssessment) {
                    this.conversion = error.storageOnlyConversion;
                    return;
                }
                if (error instanceof UserAccessError && error.existingMember?.canAssignToAssessment && this.assessmentMode) {
                    this.existingMember = error.existingMember;
                    return;
                }
                this.error = invitationFailureMessage(error);
            }
        });
    }

    close(): void {
        if (!this.busy) this.modal.destroy(this.persisted ?? { outcome: "canceled" });
    }

    private refreshResendPermission(): void {
        const saved = this.persisted;
        this.canResend = false;
        if (!saved || saved.recovery !== "resend_invitation" || !this.current()) return;
        const version = ++this.recoveryVersion;
        this.recoveryRead?.unsubscribe();
        this.recoveryRead = this.accounts.getUserAccess({ targetUserId: saved.user.id,
            ...(saved.productLicenseId !== null ? { productLicenseId: saved.productLicenseId } : {}) })
            .pipe(takeUntil(this.destroyed)).subscribe({
                next: (access) => {
                    if (version !== this.recoveryVersion || !this.current() || this.persisted !== saved) return;
                    this.canResend = access.target?.userId === saved.user.id && access.target.capabilities.canResendInvitation;
                    if (!this.canResend) this.recoveryMessage = "Resend is not available with your current permission or this member's activation state. The saved account and access remain.";
                }, error: () => {
                    if (version === this.recoveryVersion && this.current()) this.recoveryMessage = "Resend permission could not be confirmed. The account and access remain saved; an authorized user can recover the invitation from User Settings.";
                }
            });
    }

    resend(): void {
        const saved = this.persisted;
        if (!saved || !this.canResend || this.busy || !this.current()) return;
        this.setBusy(true); this.canResend = false; this.recoveryMessage = "";
        // Recheck the exact member immediately before the server-authorized resend.
        this.accounts.getUserAccess({ targetUserId: saved.user.id,
            ...(saved.productLicenseId !== null ? { productLicenseId: saved.productLicenseId } : {}) })
            .pipe(takeUntil(this.destroyed)).subscribe({
                next: (access) => {
                    if (!this.current() || this.persisted !== saved) return;
                    if (access.target?.userId !== saved.user.id || !access.target.capabilities.canResendInvitation) {
                        this.setBusy(false); this.recoveryMessage = "Resend is no longer permitted. The saved account and access remain."; return;
                    }
                    this.accounts.resendUserInvitation(saved.user.id).pipe(takeUntil(this.destroyed)).subscribe({
                        next: (result) => {
                            if (!this.current() || this.persisted !== saved) return;
                            if (result.deliveryState === "sent") {
                                this.persisted = { ...saved, outcome: "invited", deliveryState: "sent", recovery: null };
                                this.recoveryMessage = "A new activation link was sent. Use the latest invitation email.";
                            } else this.recoveryMessage = "The activation token was replaced, but the email could not be delivered. The saved account and access remain.";
                            this.setBusy(false); this.refreshResendPermission();
                        }, error: () => {
                            if (!this.current() || this.persisted !== saved) return;
                            this.recoveryMessage = "Resend was not confirmed; its activation token may have changed. The account and access remain saved. Check current permission before explicitly trying again.";
                            this.setBusy(false); this.refreshResendPermission();
                        }
                    });
                }, error: () => {
                    if (!this.current() || this.persisted !== saved) return;
                    this.setBusy(false); this.recoveryMessage = "Resend permission could not be confirmed. The saved account and access remain.";
                }
            });
    }

    private current(): boolean {
        if (this.data.sessionIdentity === assessmentTaskSessionIdentity(this.login.getUserSnapshot()) && this.data.token === this.login.getToken() &&
            (this.data.isCurrentOrigin?.() ?? true)) return true;
        this.modal.destroy({ outcome: "canceled" });
        return false;
    }

    private setBusy(value: boolean): void {
        this.busy = value;
        this.modal.updateConfig({ nzKeyboard: !value, nzClosable: !value });
    }

    private newSubmissionKey(): string {
        const bytes = new Uint8Array(24);
        globalThis.crypto.getRandomValues(bytes);
        return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
    }

    ngOnDestroy(): void { this.accessVersion += 1; this.recoveryVersion += 1; this.recoveryRead?.unsubscribe(); this.accessRead?.unsubscribe(); this.destroyed.next(); this.destroyed.complete(); }
}
